import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { money, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { printOrderReceipt } from "@/lib/printing";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUS_META } from "@/pages/app/POS";
import { Banknote, CreditCard, Printer } from "lucide-react";

export default function Orders() {
  const { user } = useAuth();
  const [current, setCurrent] = useState([]);
  const [history, setHistory] = useState([]);
  const reprint = async (o) => {
    try { const { data } = await api.post(`/orders/${o.id}/reprint`); await printOrderReceipt(data, { copy: true }); } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    const load = () => {
      api.get("/orders/current").then((r) => setCurrent(r.data)).catch(() => {});
      api.get("/orders/history").then((r) => setHistory(r.data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, []);

  const Row = ({ o, closed }) => {
    const meta = STATUS_META[o.status] || STATUS_META.OPEN;
    return (
      <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-4" data-testid="order-row">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl text-forest-deep">Маса {o.table_number}</div>
          {closed ? (
            <span className="flex items-center gap-1 text-sm text-forest">
              {o.payment?.method === "CASH" ? <Banknote className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
              {o.payment?.method === "CASH" ? "В брой" : "Карта"}
            </span>
          ) : (
            <span className={`text-xs px-2 py-1 rounded-lg border ${meta.cls}`}>{meta.label}</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1">{o.waitress_name}</div>
        <div className="mt-2 text-sm space-y-1 max-h-40 overflow-y-auto">
          {(o.items || []).map((i, idx) => (
            <div key={i.id || idx} className="flex justify-between">
              <span>{i.quantity} × {i.name_bg || i.name}{i.note ? ` (${i.note})` : ""}</span>
              <span>{money(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="gold-rule my-2" />
        <div className="flex justify-between font-bold text-forest-deep">
          <span>Общо{o.payment?.discount > 0 ? ` (-${o.payment.discount_pct}%)` : ""}</span><span>{money(o.payment?.amount ?? o.total)}</span>
        </div>
        {closed && o.closed_at && <div className="text-xs text-muted-foreground mt-1">{new Date(o.closed_at).toLocaleString()}{o.payment?.receipt_no ? ` · № ${o.payment.receipt_no}` : ""}</div>}
        {closed && user.role === "OWNER" && o.payment?.receipt_no && (
          <Button size="sm" variant="outline" onClick={() => reprint(o)} className="mt-2 gap-1 rounded-lg" data-testid={`reprint-${o.id}`}>
            <Printer className="w-4 h-4" /> Повторен печат{o.payment.reprints ? ` (${o.payment.reprints})` : ""}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="font-display text-4xl text-forest-deep">Поръчки & Плащания</h1>
      <div className="gold-rule w-24 my-4" />
      <Tabs defaultValue="current">
        <TabsList className="bg-cream-muted">
          <TabsTrigger value="current" data-testid="tab-current">Текущи ({current.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">История ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="current">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {current.map((o) => <Row key={o.id} o={o} />)}
            {current.length === 0 && <p className="text-muted-foreground">Няма отворени поръчки</p>}
          </div>
        </TabsContent>
        <TabsContent value="history">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {history.map((o) => <Row key={o.id} o={o} closed />)}
            {history.length === 0 && <p className="text-muted-foreground">Няма затворени поръчки</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
